import { defineStore } from "pinia"

import { mapSyncDraftMetaToDashboardCounts } from "@/modules/sync/syncDraftMeta"
import syncService from "@/services/syncService"
import { SyncDraftMeta, Synchronization, SyncTaskData } from "@/types/sync.type"

type SyncListState = {
  synchronizations: Synchronization[]
  synchronizationsTask: Record<string, SyncTaskData>
  synchronizationsDraftsMetaFetching: boolean
  synchronizationsDraftsMeta: Record<string, SyncDraftMeta>
  taskPollIntervals: number[]
  fetching: boolean
}

export const useSyncListStore = defineStore("syncList", {
  state: (): SyncListState => ({
    synchronizations: [],
    synchronizationsTask: {},
    synchronizationsDraftsMetaFetching: false,
    synchronizationsDraftsMeta: {},
    taskPollIntervals: [],
    fetching: false
  }),

  getters: {
    hasSynchronizations: (state) => Boolean(state.synchronizations.length)
  },

  actions: {
    async getSynchronizations() {
      this.stopTaskPolling()

      this.fetching = true
      try {
        const response = await syncService.getSynchronizations()
        this.synchronizations = response.data.results
        this.startTaskPolling()

        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      } finally {
        this.fetching = false
      }
    },

    async getSyncDraftsMeta(uuids: string[]) {
      this.synchronizationsDraftsMeta = {}
      this.synchronizationsDraftsMetaFetching = true

      await Promise.allSettled(
        uuids.map(async (uuid) => {
          try {
            const { data: sellingData } = await syncService.getOffersIsSellingInSync(uuid)
            const offersIsSellingCount = sellingData.count

            this.synchronizationsDraftsMeta = {
              ...this.synchronizationsDraftsMeta,
              [uuid]: {
                ...(this.synchronizationsDraftsMeta[uuid] || {}),
                offersIsSellingCount
              }
            }

            const { data: draftsData } = await syncService.getDraftsMeta(uuid)

            this.synchronizationsDraftsMeta = {
              ...this.synchronizationsDraftsMeta,
              [uuid]: {
                ...this.synchronizationsDraftsMeta[uuid],
                ...mapSyncDraftMetaToDashboardCounts(offersIsSellingCount, draftsData)
              }
            }
          } catch (e) {
            console.error(`Error loading meta for sync ${uuid}:`, e)
          }
        })
      )

      this.synchronizationsDraftsMetaFetching = false
    },

    startTaskPolling() {
      // Спочатку зупиняємо попередні інтервали, якщо вони є
      this.stopTaskPolling()

      const intervals: number[] = []

      // Створюємо інтервал для кожної синхронізації
      this.synchronizations.forEach((sync) => {
        const poll = () => {
          syncService
            .getTask(sync.uuid)
            .then(({ data }) => {
              // Reactivity note: this might need `set` in Vue 2 if not using standard assignment correctly,
              // but Pinia handles reactivity.
              // However, creating a new object is safer for Vue 2 reactivity system limitations if relying on it.
              this.synchronizationsTask = {
                ...this.synchronizationsTask,
                [sync.uuid]: data
              }
            })
            .catch((error) => {
              console.error(`Error polling task for sync ${sync.uuid}:`, error)
            })
        }

        const intervalId = window.setInterval(poll, 5000)
        intervals.push(intervalId)

        // Викликаємо getTask одразу при старті
        poll()
      })

      this.taskPollIntervals = intervals
    },

    stopTaskPolling() {
      this.taskPollIntervals.forEach((intervalId) => clearInterval(intervalId))
      this.taskPollIntervals = []
    }
  }
})
